import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import Joyride, { CallBackProps, STATUS, EVENTS, ACTIONS, Step } from 'react-joyride';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../services/firebase/config';
import { useAuth } from './AuthContext';
import { TourContextType, TourDefinition, TourProgress } from '../types/tour.types';
import { ALL_TOURS, getTourById } from '../data/tourDefinitions';

const TourContext = createContext<TourContextType | null>(null);

/**
 * Custom styles for Joyride to match PropCloudAI design
 */
const joyrideStyles = {
  options: {
    primaryColor: '#6eb041',
    textColor: '#374151',
    backgroundColor: '#ffffff',
    overlayColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 10000,
  },
  buttonNext: {
    backgroundColor: '#6eb041',
    borderRadius: '8px',
    padding: '8px 16px',
  },
  buttonBack: {
    color: '#6b7280',
    marginRight: '8px',
  },
  buttonSkip: {
    color: '#9ca3af',
  },
  tooltip: {
    borderRadius: '12px',
    padding: '20px',
  },
  tooltipTitle: {
    fontSize: '18px',
    fontWeight: 600,
    marginBottom: '8px',
  },
  tooltipContent: {
    fontSize: '14px',
    lineHeight: '1.5',
  },
  spotlight: {
    borderRadius: '8px',
  },
};

interface TourProviderProps {
  children: React.ReactNode;
}

export function TourProvider({ children }: TourProviderProps) {
  const { userProfile } = useAuth();

  // Tour state
  const [activeTour, setActiveTour] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [steps, setSteps] = useState<Step[]>([]);

  // Welcome modal state
  const [showWelcomeModal, setShowWelcomeModal] = useState(false);

  // Convert our TourStep to Joyride Step format.
  // We deliberately put "Step X of N" INSIDE the tooltip content (top of the
  // card) rather than rely on joyride's built-in `showProgress`. The built-in
  // version appends "(Step X of N)" to the next button's text, which the
  // browser then re-shows as a native title-attribute tooltip on hover —
  // looks like a duplicated label. Rendering it ourselves avoids that.
  const convertToJoyrideSteps = (tourSteps: TourDefinition['steps']): Step[] => {
    const total = tourSteps.length;
    return tourSteps.map((step, idx) => ({
      target: step.target,
      content: (
        <div>
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-primary/80">
            Step {idx + 1} of {total}
          </div>
          <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
          <p className="text-gray-600">{step.content}</p>
        </div>
      ),
      placement: step.placement as Step['placement'],
      spotlightClicks: step.spotlightClicks,
      disableBeacon: step.disableBeacon ?? true,
      hideCloseButton: step.hideCloseButton,
      showSkipButton: step.showSkipButton ?? true,
      showProgress: false,
    }));
  };

  // Start a tour
  const startTour = useCallback((tourId: string) => {
    const tour = getTourById(tourId);
    if (!tour) {
      console.warn(`Tour not found: ${tourId}`);
      return;
    }

    setActiveTour(tourId);
    setSteps(convertToJoyrideSteps(tour.steps));
    setCurrentStepIndex(0);
    setIsRunning(true);
  }, []);

  // Stop current tour
  const stopTour = useCallback(() => {
    setIsRunning(false);
    setActiveTour(null);
    setCurrentStepIndex(0);
    setSteps([]);
  }, []);

  // Skip current tour
  const skipTour = useCallback(async () => {
    if (!userProfile?.id || !activeTour) return;

    // Update progress in Firestore. A skip also marks the tour as seen
    // (completedTours) so it never auto-starts again — it stays replayable
    // from Settings.
    const userRef = doc(db, 'users', userProfile.id);
    const updates: Record<string, any> = {
      [`tourPreferences.tourProgress.${activeTour}`]: {
        stepsCompleted: currentStepIndex,
        totalSteps: steps.length,
        lastStepAt: Timestamp.now(),
      },
    };
    const completedTours = userProfile.tourPreferences?.completedTours ?? [];
    if (!completedTours.includes(activeTour)) {
      updates['tourPreferences.completedTours'] = [...completedTours, activeTour];
    }

    try {
      await updateDoc(userRef, updates);
    } catch (error) {
      console.error('Failed to save tour preferences:', error);
    }

    stopTour();
  }, [userProfile?.id, userProfile?.tourPreferences?.completedTours, activeTour, currentStepIndex, steps.length, stopTour]);

  // Navigate steps
  const nextStep = useCallback(() => {
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex(prev => prev + 1);
    }
  }, [currentStepIndex, steps.length]);

  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(prev => prev - 1);
    }
  }, [currentStepIndex]);

  const goToStep = useCallback((index: number) => {
    if (index >= 0 && index < steps.length) {
      setCurrentStepIndex(index);
    }
  }, [steps.length]);

  // Check if tour is completed
  const isTourCompleted = useCallback((tourId: string): boolean => {
    return userProfile?.tourPreferences?.completedTours?.includes(tourId) ?? false;
  }, [userProfile?.tourPreferences?.completedTours]);

  // Get tour progress
  const getTourProgress = useCallback((tourId: string): TourProgress | null => {
    return userProfile?.tourPreferences?.tourProgress?.[tourId] ?? null;
  }, [userProfile?.tourPreferences?.tourProgress]);

  // Dismiss welcome modal
  const dismissWelcomeModal = useCallback(async (skipped: boolean) => {
    if (!userProfile?.id) return;

    const userRef = doc(db, 'users', userProfile.id);
    await updateDoc(userRef, {
      'tourPreferences.welcomeModalShown': true,
      'tourPreferences.welcomeModalShownAt': Timestamp.now(),
      'tourPreferences.welcomeModalSkipped': skipped,
    });

    setShowWelcomeModal(false);
  }, [userProfile?.id]);

  // Handle Joyride callback
  const handleJoyrideCallback = useCallback(async (data: CallBackProps) => {
    const { status, type, index, action } = data;

    // Update step index
    if (type === EVENTS.STEP_AFTER && action === ACTIONS.NEXT) {
      setCurrentStepIndex(index + 1);
    } else if (type === EVENTS.STEP_AFTER && action === ACTIONS.PREV) {
      setCurrentStepIndex(index - 1);
    }

    // The tooltip's X button emits action CLOSE while status stays "running" —
    // treat it exactly like a skip, otherwise the controlled tour never ends.
    const closedViaX = type === EVENTS.STEP_AFTER && action === ACTIONS.CLOSE;

    // Handle tour completion / dismissal
    if (status === STATUS.FINISHED || status === STATUS.SKIPPED || closedViaX) {
      const finished = status === STATUS.FINISHED;

      if (userProfile?.id && activeTour) {
        const userRef = doc(db, 'users', userProfile.id);
        const updates: Record<string, any> = {
          [`tourPreferences.tourProgress.${activeTour}`]: {
            stepsCompleted: finished ? steps.length : currentStepIndex,
            totalSteps: steps.length,
            ...(finished && { completedAt: Timestamp.now() }),
            lastStepAt: Timestamp.now(),
          },
        };

        // Finished, skipped or closed: mark the tour as seen so it never
        // auto-starts again. It stays replayable from Settings.
        const completedTours = userProfile.tourPreferences?.completedTours ?? [];
        if (!completedTours.includes(activeTour)) {
          updates['tourPreferences.completedTours'] = [...completedTours, activeTour];
        }

        try {
          await updateDoc(userRef, updates);
        } catch (error) {
          console.error('Failed to save tour preferences:', error);
        }
      }

      stopTour();
    }
  }, [userProfile?.id, userProfile?.tourPreferences?.completedTours, activeTour, currentStepIndex, steps.length, stopTour]);

  const contextValue: TourContextType = {
    activeTour,
    currentStepIndex,
    isRunning,
    availableTours: ALL_TOURS,
    startTour,
    stopTour,
    skipTour,
    nextStep,
    prevStep,
    goToStep,
    isTourCompleted,
    getTourProgress,
    showWelcomeModal,
    setShowWelcomeModal,
    dismissWelcomeModal,
  };

  return (
    <TourContext.Provider value={contextValue}>
      {children}
      {isRunning && (
        <Joyride
          steps={steps}
          stepIndex={currentStepIndex}
          run={isRunning}
          continuous
          showSkipButton
          scrollToFirstStep
          disableOverlayClose
          callback={handleJoyrideCallback}
          styles={joyrideStyles}
          locale={{
            back: 'Back',
            close: 'Close',
            last: 'Finish',
            next: 'Next',
            skip: "Skip — don't show again",
          }}
        />
      )}
    </TourContext.Provider>
  );
}

export function useTour(): TourContextType {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
}

export default TourContext;
